import missionCodec from '@m1/mission-codec';
import useMountEffect from '@restart/hooks/useMountEffect';
import useUpdateEffect from '@restart/hooks/useUpdateEffect';
import classNames from 'classnames/bind';
import React, { useState } from 'react';
import { RiUpload2Line } from 'react-icons/ri';
import { useDispatch, useStore } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import styles from './common.module.scss';

import actions from '@/actions';
import ConfirmSlider from '@/components/ui/ConfirmSlider';
import ProgressBar from '@/components/ui/ProgressBar';
import FenceConverter from '@/helpers/FenceConverter';
import useMissionCommand from '@/hooks/command/mavlink/useMissionCommand';
import { NotifierService as notifier } from '@/libs/Notifier';
import { executeMavLinkCommand } from '@/utils/CommandUtils';

const cx = classNames.bind(styles);

const Panel = ({ data: robots }) => {
  const dispatch = useDispatch();
  const store = useStore();
  const [isProcessing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(null);
  const missionCommand = useMissionCommand();

  useMountEffect(() => {
    const robot = robots[0];
    const mission = store.getState().missions[robot.id];

    // 미션 업로드 전 사전 검증: FC 다운로드 미션은 id 미존재
    if (!mission || !mission.id || mission.commands.length === 0) {
      notifier.error(`Please load the mission to the ${robot.name} robot first.`);
      dispatch(actions.command.resetActivePanels());
    }
  });

  //#region Process Functions
  const process = async (robot) => {
    const { commands, fences } = store.getState().missions[robot.id];

    try {
      const commandMessages = getCommandMessages(robot, commands);
      const fenceMessages = FenceConverter.toMavLink(fences);

      const total = commandMessages.length + fenceMessages.length;
      setProgress({ current: 0, total });

      // Commands 업로드
      await processUpload(robot, commandMessages, 0);

      // Fences 업로드
      await processUpload(robot, fenceMessages, 1);

      onSuccess(robot);
    } catch (error) {
      onFailure(robot);
    }
  };

  const getCommandMessages = (robot, commands) => {
    switch (robot.model.maker) {
      case 'ArduPilot':
        return missionCodec.mavlink.ardupilot.encode(commands);
      case 'PX4':
        return missionCodec.mavlink.px4.encode(commands);
      default:
        return;
    }
  };

  const processUpload = async (robot, messages, missionType) => {
    if (messages.length === 0) return;

    await executeMavLinkCommand(robot, missionCommand.sendCount(messages.length, missionType));

    for (let seq = 0; seq < messages.length; seq++) {
      await executeMavLinkCommand(robot, missionCommand.sendItem(messages[seq], missionType, seq, messages.length));
      setProgress((prev) => ({ ...prev, current: prev.current + 1 }));
    }
  };
  //#endregion

  //#region Result Handlers
  // 성공 시 처리
  const onSuccess = (robot) => {
    notifier.success(`Mission uploaded successfully from ${robot.name}.`);
    dispatch(actions.missions.setSynced(robot.id, true));
    setProcessing(false);
  };

  // 실패 시 처리
  const onFailure = (robot) => {
    notifier.error(`Failed to upload mission on ${robot.name}.`);
    setProcessing(false);
  };
  //#endregion

  useUpdateEffect(() => {
    if (!isProcessing) {
      dispatch(actions.command.resetActivePanels());
      return;
    }

    process(robots[0]);
  }, [isProcessing]);

  const doCommand = () => {
    // 처리 중 상태로 변경
    setProcessing(true);
  };

  return (
    <div className={cx('container')}>
      <div className={cx('desc')}>Uploads planned mission to the vehicle</div>
      {!progress && <ConfirmSlider width="100%" height={40} onConfirm={doCommand} labelStyle={{ fontSize: 12 }} />}
      {progress && <ProgressBar current={progress.current} total={progress.total} />}
    </div>
  );
};

const Command = {};
Command.id = uuidv4();
Command.name = 'Upload';
Command.displayMode = 'sidebar';
Command.singleRobotOnly = true;
Command.commanderOnly = true;
Command.Icon = RiUpload2Line;
Command.Panel = Panel;

export default Command;
